import { usePlayerProvider } from 'providers/PlayerProvider';
import { useStoreState } from 'providers/StoreStateContext';
import { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { ITrackArtistResponse } from 'services/tracks/types';

export interface IAudioPlayerTrack {
  title: string;
  artists?: ITrackArtistResponse[];
  imageUrl?: string | null;
}

export const useAudioPlayer = (
  playbackUrl: string | null,
  track?: IAudioPlayerTrack
) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const prevUrlRef = useRef<string | null>(null);
  const shouldAutoPlayRef = useRef(true);
  const { trackVolume, setTrackVolume } = useStoreState();

  const {
    isTrackPlaying: isPlaying,
    setIsTrackPlaying: setIsPlaying,
    setIsVideoPlaying,
    registerAudioControls,
  } = usePlayerProvider();

  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = trackVolume;
    }
  }, [trackVolume]);

  const playAudio = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio?.src) {
      setIsPlaying(true);
      return;
    }

    setIsVideoPlaying(false);
    setIsBuffering(true);

    try {
      await audio.play();
      setIsPlaying(true);
      setIsBuffering(false);
    } catch (err) {
      setIsBuffering(false);
      const name = err instanceof Error ? err.name : '';
      if (name !== 'AbortError') {
        setIsPlaying(false);
      }
    }
  }, [setIsPlaying, setIsVideoPlaying]);

  const pauseAudio = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
    }
    setIsPlaying(false);
    setIsBuffering(false);
  }, [setIsPlaying]);

  useEffect(() => {
    registerAudioControls({
      play: () => {
        void playAudio();
      },
      pause: pauseAudio,
    });

    return () => {
      registerAudioControls(null);
    };
  }, [registerAudioControls, playAudio, pauseAudio]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (!playbackUrl) {
      audio.pause();
      audio.removeAttribute('src');
      setCurrentTime(0);
      setDuration(0);
      setIsBuffering(false);
      prevUrlRef.current = null;
      shouldAutoPlayRef.current = true;
      return;
    }

    const isNewUrl = prevUrlRef.current !== playbackUrl;
    prevUrlRef.current = playbackUrl;

    if (isNewUrl) {
      setIsVideoPlaying(false);
      setIsBuffering(true);
      audio.src = playbackUrl;
      audio.load();

      if (shouldAutoPlayRef.current) {
        void playAudio();
      }
      return;
    }

    if (isPlaying && audio.paused) {
      void playAudio();
    } else if (!isPlaying && !audio.paused) {
      audio.pause();
      setIsBuffering(false);
    }
  }, [playbackUrl, isPlaying, playAudio, setIsPlaying, setIsVideoPlaying]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || !track?.title) {
      return;
    }

    navigator.mediaSession.metadata = new MediaMetadata({
      title: track.title,
      artist: Array.from(
        new Set(
          track.artists?.map(artist => artist.nickname).filter(Boolean) ?? []
        )
      ).join(', '),
      artwork: track.imageUrl
        ? [
            {
              src: track.imageUrl,
              sizes: '512x512',
              type: 'image/jpeg',
            },
          ]
        : [],
    });

    navigator.mediaSession.setActionHandler('play', () => {
      void playAudio();
    });

    navigator.mediaSession.setActionHandler('pause', () => {
      pauseAudio();
    });

    navigator.mediaSession.setActionHandler('seekto', details => {
      if (
        audioRef.current &&
        details.seekTime !== undefined &&
        details.seekTime !== null
      ) {
        audioRef.current.currentTime = details.seekTime;
        setCurrentTime(details.seekTime);
      }
    });

    return () => {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = null;
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('seekto', null);
      }
    };
  }, [track, playAudio, pauseAudio]);

  const handleLoadedMetadata = (event: SyntheticEvent<HTMLAudioElement>) => {
    setDuration(event.currentTarget.duration);
    setIsBuffering(false);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current && !isDragging) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleSeekStart = () => {
    setIsDragging(true);
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  const handleSeekEnd = (newTime: number) => {
    setIsDragging(false);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      if (isPlaying) {
        void playAudio();
      }
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    setTrackVolume(newVolume);
  };

  const togglePlay = () => {
    // Call play/pause in the click handler so the browser keeps the user gesture.
    if (isPlaying) {
      pauseAudio();
      return;
    }

    void playAudio();
  };

  const toggleMute = () => {
    handleVolumeChange(trackVolume > 0 ? 0 : 1);
  };

  const handleEnded = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setCurrentTime(0);
    setIsPlaying(false);
    setIsBuffering(false);
  };

  const handleWaiting = useCallback(() => {
    setIsBuffering(true);
  }, []);

  const handlePlaying = useCallback(() => {
    setIsBuffering(false);
  }, []);

  const handleCanPlay = useCallback(() => {
    setIsBuffering(false);
  }, []);

  const handleError = useCallback(() => {
    setIsBuffering(false);
    setIsPlaying(false);
  }, [setIsPlaying]);

  return {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    trackVolume,
    handleLoadedMetadata,
    handleTimeUpdate,
    togglePlay,
    handleSeekStart,
    handleSeekEnd,
    handleVolumeChange,
    toggleMute,
    setCurrentTime,
    handleEnded,
    isBuffering,
    handleCanPlay,
    handlePlaying,
    handleWaiting,
    handleError,
    playAudio,
    pauseAudio,
  };
};
